import Heading, { HeadingLevels } from '@/components/atoms/legacy/Heading';
import { Skeleton } from '@/components/atoms/Skeleton';
import ClampedText from '@/components/molecules/ClampedText';
import useWindowSize from '@/hooks/useWindowSize';
import { useValidateTrim } from '@/hooks/queries/compare';
import {
  CHOOSE_TRIM,
  MSRP,
  PRICE_COMING_SOON,
  STARTING,
} from '@/lib/constants';
import { formatNumberWithCommas } from '@/lib/formatNumberWithCommas';
import { priceFormatter } from '@/lib/priceFormatter';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface TrimListProps {
  ModelId: number;
  MSRP: number;
  Trim: string;
  Model: string;
  Make: string;
  Year: number;
}

export interface EVCompareTrimSelectionProps {
  activeTrimIndex: number;
  trimList: TrimListProps[];
  handleTrimClick: (trim: string, MSRP: number, index: number) => void;
}

export const EVCompareTrimSelection: React.FC<EVCompareTrimSelectionProps> = ({
  trimList,
  activeTrimIndex,
  handleTrimClick,
}) => {
  const { isMobile } = useWindowSize();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [validatingTrimIndex, setValidatingTrimIndex] = useState<number | null>(
    null
  );
  const { mutate: validateTrim, isPending } = useValidateTrim();

  const handleTrimValidation = useCallback(
    (trim: TrimListProps, index: number) => {
      setValidatingTrimIndex(index);
      validateTrim(
        {
          year: trim.Year,
          make: trim.Make,
          model: trim.Model,
          trim: trim.Trim,
        },
        {
          onSuccess: () => {
            handleTrimClick(trim.Trim, trim.MSRP, index);
            setValidatingTrimIndex(null);
          },
          onError: () => {
            setValidatingTrimIndex(null);
          },
        }
      );
    },
    [validateTrim, handleTrimClick]
  );

  useEffect(() => {
    if (sectionRef.current && trimList.length > 0) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [trimList.length]);

  return (
    <>
      <Heading
        className="mx-xs ml-xs text-h4Regular l:mb-l"
        level={HeadingLevels.H4}
      >
        {CHOOSE_TRIM}
      </Heading>
      <section>
        <div
          className={`mb-[64px] grid overflow-scroll pb-xl ${
            isMobile ? 'grid-cols-2' : 'grid-cols-3'
          }`}
          ref={sectionRef}
        >
          {trimList.length === 0 ? (
            <>
              {Array.from({ length: isMobile ? 4 : 8 }).map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="m-xs flex h-fit-content flex-col justify-center p-l"
                >
                  <Skeleton className="mb-xs h-l w-full rounded-xsmall" />
                  <Skeleton className="h-l w-3/4 rounded-xsmall" />
                </div>
              ))}
            </>
          ) : (
            // Display all trims without pre-validation
            trimList.map((trim, index) => (
              <div
                className={`align-center relative m-xs flex h-fit-content cursor-pointer flex-col justify-center rounded-xsmall border border-solid p-l ${
                  index === activeTrimIndex
                    ? 'border-brandElectric bg-green-light'
                    : 'border-neutral-300'
                }`}
                key={index}
                onClick={() => {
                  if (!isPending && validatingTrimIndex === null) {
                    handleTrimValidation(trim, index);
                  }
                }}
              >
                {validatingTrimIndex === index && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70">
                    <div className="size-l animate-spin rounded-full border-[8px] border-t-[8px] border-neutral-300 border-t-brandElectric"></div>
                  </div>
                )}
                <p className="whitespace-nowrap text-body2Regular l:text-body1Regular">
                  {trim.Trim}
                </p>
                <ClampedText className="text-body2Regular text-neutral-600 l:text-body1Regular">
                  {trim.MSRP === 0
                    ? priceFormatter(trim.MSRP, PRICE_COMING_SOON)
                    : `${STARTING} ${MSRP} $${formatNumberWithCommas(
                        trim.MSRP
                      )}`}
                </ClampedText>
              </div>
            ))
          )}
        </div>
      </section>
    </>
  );
};
